/**
 * Texts, the headers of files and deletion marks (docs/FORMAT.md §2.1, §3, §4). Every message is padded, sealed under the
 * message key with additional data that binds it to its bridge, its id, its side, its kind and its place in its writer's
 * run, so a server that moves, replays or relabels a message makes decryption fail (docs/ENCRYPTION.md).
 */
import { toBase64Url } from './base64url';
import {
  buildMessageEnvelope,
  messageAad,
  messageFlags,
  parseMessageEnvelope,
  type MessageBinding,
  type MessagePart,
  type Position,
} from './envelope';
import { FILE_NAME_MAX, FILE_NONCE_PATTERN, FLAG_DELETION, IV_BYTES, SENDER_BYTES, SEQ_BYTES } from './labels';
import { padPlaintext, unpadPlaintext } from './padding';
import { DecryptError, openWithIv, sealWithIv } from './primitives';
import { randomBytes } from './random';
import { releasePosition, reservePosition } from './sequence';
import { messageKey } from './subkeys';

export type { MessageBinding, Position } from './envelope';

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true });

/**
 * Seals `data` as a message of `part`. `position` is the place in this page's run for the bridge, taken now unless the caller
 * has one. `iv` is for the test vectors only: a real caller never passes one.
 */
export async function sealMessage(
  key: CryptoKey,
  part: MessagePart,
  data: Uint8Array,
  binding: MessageBinding,
  position?: Position,
  iv: Uint8Array<ArrayBuffer> = randomBytes(IV_BYTES),
): Promise<string> {
  const flags = messageFlags(part);
  const padded = padPlaintext(data);
  // checked before a number is taken, so a refused message does not leave a hole in the run
  messageAad(binding, part, flags, { sender: new Uint8Array(SENDER_BYTES), seq: 0 });
  const taken = position === undefined ? reservePosition(binding.bridgeId) : null;
  const place = position ?? taken;
  if (place === null) throw new RangeError('No place for the message.');
  try {
    const sealed = await sealWithIv(await messageKey(key), iv, padded, messageAad(binding, part, flags, place));
    return toBase64Url(buildMessageEnvelope(flags, place, iv, sealed));
  } catch (cause) {
    if (taken !== null) releasePosition(binding.bridgeId, taken);
    throw cause;
  }
}

/** The data of a message of `part` and its place, or a DecryptError: another bridge, id, side, kind, key or place, a changed byte. */
export async function openMessage(key: CryptoKey, encoded: string, binding: MessageBinding, part: MessagePart): Promise<{ data: Uint8Array<ArrayBuffer>; position: Position }> {
  const envelope = parseMessageEnvelope(encoded);
  if (envelope.flags !== messageFlags(part)) throw new DecryptError();
  const padded = await openWithIv(await messageKey(key), envelope.iv, envelope.sealed, messageAad(binding, part, envelope.flags, envelope.position));
  return { data: unpadPlaintext(padded), position: envelope.position };
}

// ------------------------------------------------------------------ texts

export function encryptText(key: CryptoKey, text: string, binding: MessageBinding, position?: Position): Promise<string> {
  return sealMessage(key, 'text', encoder.encode(text), binding, position);
}

export async function decryptText(key: CryptoKey, encoded: string, binding: MessageBinding): Promise<string> {
  const { data } = await openMessage(key, encoded, binding, 'text');
  try {
    return decoder.decode(data);
  } catch {
    throw new DecryptError('Invalid text.');
  }
}

// ----------------------------------------------------------- file headers

/** What a file message's encrypted header holds. */
export interface FileHeader {
  readonly name: string;
  readonly type: string;
  /** Plaintext bytes. */
  readonly size: number;
  /** Plaintext bytes per chunk (the last chunk may be shorter). */
  readonly chunkBytes: number;
  /** Random per-file value bound into every chunk and into the file's key, so chunks cannot be swapped between files. */
  readonly nonce: string;
}

export function encryptFileHeader(key: CryptoKey, header: FileHeader, binding: MessageBinding, position?: Position): Promise<string> {
  return sealMessage(key, 'file-header', encoder.encode(JSON.stringify(header)), binding, position);
}

// C0 and C1 controls, DEL, the line and paragraph separators, and every bidirectional mark, override and isolate (U+202E turns "invoice<U+202E>fdp.exe" into "invoiceexe.pdf")
// eslint-disable-next-line no-control-regex
const UNSAFE_NAME_CHARACTERS = /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u2028\u2029\u202a-\u202e\u2066-\u2069]/gu;

/** A file name from a header that a stranger wrote: no control or bidi characters, at most 255 characters. */
export function sanitizeFileName(name: string): string {
  return Array.from(name.replace(UNSAFE_NAME_CHARACTERS, '')).slice(0, FILE_NAME_MAX).join('');
}

export async function decryptFileHeader(key: CryptoKey, encoded: string, binding: MessageBinding): Promise<FileHeader> {
  const { data } = await openMessage(key, encoded, binding, 'file-header');
  let parsed: unknown;
  try {
    parsed = JSON.parse(decoder.decode(data));
  } catch {
    throw new DecryptError('Invalid file header.');
  }
  if (typeof parsed !== 'object' || parsed === null) throw new DecryptError('Invalid file header.');
  const { name, type, size, chunkBytes, nonce } = parsed as Record<string, unknown>;
  if (typeof name !== 'string' || typeof type !== 'string' || typeof size !== 'number' || typeof chunkBytes !== 'number' || typeof nonce !== 'string') {
    throw new DecryptError('Invalid file header.');
  }
  if (!Number.isSafeInteger(size) || size < 0 || !Number.isSafeInteger(chunkBytes) || chunkBytes < 1) throw new DecryptError('Invalid file header.');
  if (!FILE_NONCE_PATTERN.test(nonce)) throw new DecryptError('Invalid file header.');
  return { name: sanitizeFileName(name), type, size, chunkBytes, nonce };
}

// ---------------------------------------------------------- deletion marks

/** A deletion mark: a message whose plaintext is the place of the message the owner is about to remove (docs/FORMAT.md §5). */
export function encryptDeletion(key: CryptoKey, removed: Position, binding: MessageBinding, position?: Position): Promise<string> {
  const data = new Uint8Array(SENDER_BYTES + SEQ_BYTES);
  data.set(removed.sender, 0);
  new DataView(data.buffer).setUint32(SENDER_BYTES, removed.seq);
  return sealMessage(key, 'deletion', data, binding, position);
}

export async function decryptDeletion(key: CryptoKey, encoded: string, binding: MessageBinding): Promise<Position> {
  const { data } = await openMessage(key, encoded, binding, 'deletion');
  if (data.length !== SENDER_BYTES + SEQ_BYTES) throw new DecryptError('Invalid deletion mark.');
  return { sender: data.slice(0, SENDER_BYTES), seq: new DataView(data.buffer, data.byteOffset, data.byteLength).getUint32(SENDER_BYTES) };
}

// -------------------------------------------------------------- verifying

/** What a message is once it has been opened: its authenticated place, and the place it removes if it is a deletion mark. */
export interface VerifiedMessage {
  readonly position: Position;
  readonly removes: Position | null;
}

/**
 * Opens a message of the kind the server says (`text`, or a file's header) and returns the place that was authenticated, not
 * the one the envelope shows. A message whose deletion flag is set is opened as a deletion mark and as nothing else, so a
 * flag changed on the way makes it fail, not vanish.
 */
export async function verifyMessage(key: CryptoKey, encoded: string, binding: MessageBinding, kind: 'text' | 'file'): Promise<VerifiedMessage> {
  if (kind === 'file') return { position: (await openMessage(key, encoded, binding, 'file-header')).position, removes: null };
  if (peekMessage(encoded).deletion) {
    const { data, position } = await openMessage(key, encoded, binding, 'deletion');
    if (data.length !== SENDER_BYTES + SEQ_BYTES) throw new DecryptError('Invalid deletion mark.');
    return { position, removes: { sender: data.slice(0, SENDER_BYTES), seq: new DataView(data.buffer, data.byteOffset, data.byteLength).getUint32(SENDER_BYTES) } };
  }
  return { position: (await openMessage(key, encoded, binding, 'text')).position, removes: null };
}

// ----------------------------------------------------------------- peeking

/**
 * What a message envelope says about itself without a key: who wrote it, in which place, and whether it is a deletion mark.
 * Nothing here is authenticated until the message is opened; it is only used to order and to pick what to open.
 */
export function peekMessage(encoded: string): { position: Position; deletion: boolean } {
  const { flags, position } = parseMessageEnvelope(encoded);
  return { position, deletion: (flags & FLAG_DELETION) !== 0 };
}
